import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * TutorialModal - A step-through modal for onboarding tutorials
 *
 * Props:
 * - isOpen: boolean - whether the modal is visible
 * - tutorialKey: string - the tutorial identifier
 * - steps: array of step objects { id, title, content, bullets?, icon? }
 * - onComplete: function - called when user completes the tutorial
 * - onSkip: function - called when user skips the tutorial
 * - onClose: function - called when modal is closed
 */
function TutorialModal({ isOpen, tutorialKey, steps, onComplete, onSkip, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step - complete the tutorial
      onComplete?.(tutorialKey);
    }
  }, [currentStep, steps.length, onComplete, tutorialKey]);

  const handleSkip = useCallback(() => {
    onSkip?.(tutorialKey);
  }, [onSkip, tutorialKey]);

  const handleClose = useCallback(() => {
    // Closing is the same as skipping
    onSkip?.(tutorialKey);
    onClose?.();
  }, [onSkip, onClose, tutorialKey]);

  if (!isOpen || !steps || steps.length === 0) {
    return null;
  }

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Progress bar */}
        <div className="h-1 bg-gray-200">
          <div
            className="h-full bg-primary-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Icon */}
          {step.icon && (
            <div className="text-4xl mb-4">{step.icon}</div>
          )}

          {/* Title */}
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {step.title}
          </h2>

          {/* Main content */}
          <p className="text-gray-600 mb-4 leading-relaxed">
            {step.content}
          </p>

          {/* Bullet points */}
          {step.bullets && step.bullets.length > 0 && (
            <ul className="space-y-2 mb-4">
              {step.bullets.map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-2 text-gray-600">
                  <span className="text-primary-500 mt-1">•</span>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Step counter */}
          <div className="text-sm text-gray-400 mb-6">
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>

        {/* Actions */}
        <div className="px-8 pb-8 flex justify-between items-center">
          <button
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700 text-sm font-medium"
          >
            Skip
          </button>

          <div className="flex gap-3">
            {!isFirstStep && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition"
            >
              {isLastStep ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

TutorialModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  tutorialKey: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      bullets: PropTypes.arrayOf(PropTypes.string),
      icon: PropTypes.string,
    })
  ).isRequired,
  onComplete: PropTypes.func,
  onSkip: PropTypes.func,
  onClose: PropTypes.func,
};

export default TutorialModal;
